import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';

/**
 * Searchable department picker backed by MST_Department (via
 * /api/econtract/departments). Type a code or part of a name; the value saved
 * on the form is the department code.
 */
const DepartmentSelect = ({ name, value, onChange, disabled, hasError }) => {
  const [departments, setDepartments] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/econtract/departments`)
      .then(resp => { if (!cancelled && Array.isArray(resp.data?.departments)) setDepartments(resp.data.departments); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const selected = departments.find(d => d.code === value);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter(d => `${d.code} ${d.name} ${d.group}`.toLowerCase().includes(q));
  }, [departments, query]);

  const choose = (dept) => {
    onChange(name, dept.code);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && open && filtered[active]) { e.preventDefault(); choose(filtered[active]); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  // Closed: show the chosen department. Open: show what the user is typing.
  const shown = open ? query : (selected ? `${selected.code} · ${selected.name}` : (value || ''));
  const unknownValue = Boolean(value) && !selected && departments.length > 0;

  return (
    <div className="rs-combo" ref={wrapRef}>
      <div className="rs-search">
        <i className="bi bi-search"></i>
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${name}-listbox`}
          autoComplete="off"
          name={name}
          value={shown}
          disabled={disabled}
          placeholder={loadFailed ? 'โหลดรายการแผนกไม่สำเร็จ' : 'ค้นหารหัสหรือชื่อแผนก...'}
          className={`rs-input${hasError ? ' rs-input--error rental-form__input--error' : ''}`}
          onFocus={() => { setOpen(true); setActive(0); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
          onKeyDown={onKeyDown}
        />
        <button type="button" tabIndex={-1} aria-label="เปิดรายการแผนก" disabled={disabled}
          className="rs-combo__chevron" onMouseDown={(e) => e.preventDefault()}
          onClick={() => { setOpen(o => !o); setActive(0); }}>
          <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </button>
      </div>
      {unknownValue && !open && (
        <div className="rs-help rs-help--warn">รหัส "{value}" ไม่อยู่ในรายการแผนก — เลือกใหม่จากรายการ</div>
      )}
      {open && !disabled && (
        <div className="rs-options rs-combo__list" role="listbox" id={`${name}-listbox`}>
          {departments.length === 0 && !loadFailed && <div className="rs-options__empty">กำลังโหลด...</div>}
          {departments.length > 0 && filtered.length === 0 && <div className="rs-options__empty">ไม่พบแผนกที่ค้นหา</div>}
          {filtered.map((d, i) => (
            <button
              type="button"
              role="option"
              aria-selected={d.code === value}
              key={d.id}
              className={`rs-option${d.code === value ? ' is-selected' : ''}${i === active ? ' is-active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(d)}
            >
              <span className="rs-option__title">{d.code} · {d.name}</span>
              <span className="rs-option__meta">{d.group || '—'}</span>
              {d.code === value && <i className="bi bi-check-circle-fill"></i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DepartmentSelect;
